import { ChangeDetectorRef, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import {
  CYCBanner,
  PolicyType,
  IStrapiDocuments,
  IStrapiDocument,
  IPolicyDocumentYearChange,
} from 'src/app/interfaces/strapi';

import { UserService } from 'src/app/services/user.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { User } from 'src/app/shared/models/user.model';
import { ResidenceService } from 'src/app/services/residence.service';
import { Residence } from 'src/app/shared/models/residence.model';
import { ApiSearchResponse } from 'src/app/interfaces/api';
import StrapiService from 'src/app/services/strapi.service';
import { getPolicyChangeYear } from 'src/app/shared/utils/date-utils';
import { NgIf, NgFor } from '@angular/common';
import { AuthService } from '../../../services/auth.service';

enum CycEnum {
  CERTIFICATE = 'CERTIFICATE',
  POLICY_WORDING = 'POLICY WORDING',
  KEY_WORDING = 'KEY WORDING',
}

@Component({
    selector: 'app-confirm-your-cover',
    templateUrl: './confirm-your-cover.component.html',
    styleUrls: ['./confirm-your-cover.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [NgIf, NgFor],
})
export class ConfirmYourCoverComponent extends PageComponent implements OnInit {
  public bannerData: CYCBanner | null = null;

  public showCover = true;

  public updatedAccommodation: Residence = <Residence>{};

  public isLoading: boolean = false;

  public certificateUrl: string | null = '';

  public keyWordingUrl: string | null = '';

  public policyWordingUrl: string | null = '';

  public policyYearChangeMessage: string = '';

  public policyChangeYear: string = '';

  public showPolicyChange: boolean = false;

  public cdr = inject(ChangeDetectorRef);

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,

    public override readonly strapi: StrapiService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    public readonly residenceService: ResidenceService,
    public readonly auth: AuthService
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.strapi
      .getDocumentByUID('confirm-your-cover', 'cyc', { populate: '*' })
      .subscribe((banner: IStrapiDocuments<CYCBanner>) => {
        this.parseCYCBanner(banner);
        this.cdr.detectChanges();
      });

    this.strapi
      .getPolicyDocumentYearChange()
      .subscribe((response: IStrapiDocument<IPolicyDocumentYearChange>) => {
        this.showPolicyChange = !!response.data.attributes.message;
        this.policyYearChangeMessage = `<h4 class="no-margin">${response.data.attributes.message}<h4>`;
        const currentYear = getPolicyChangeYear();
        this.policyChangeYear = `<span>September ${currentYear}</span>`;
        this.cdr.detectChanges();
      });

    this.residenceService
      .getAccommodationByUUID(this.auth.currentUser?.accommodation?.uuid ?? '')
      .pipe()
      .subscribe({
        next: (data: Partial<ApiSearchResponse>) => {
          this.updatedAccommodation = <Residence>data.data;

          this.updateConfirmCover(
            this.updatedAccommodation.covered,
            this.updatedAccommodation.wellbeing
          );
        },
        complete: () => {
          this.isLoading = false;
          this.processPolicyDocuments();
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Sets the policy document urls
   */
  private processPolicyDocuments(): void {
    const docs = this.updatedAccommodation.policyDocuments;

    if (docs) {
      docs.forEach((document) => {
        switch (document.type.toUpperCase()) {
          case CycEnum.CERTIFICATE:
            if (document.file_url) {
              this.certificateUrl = `${
                document.file_url
              }?timestamp=${Date.now()}`;
              break;
            }
            this.certificateUrl = null;
            break;
          case CycEnum.KEY_WORDING:
            if (document.file_url) {
              this.keyWordingUrl = `${
                document.file_url
              }?timestamp=${Date.now()}`;
              break;
            }
            this.keyWordingUrl = null;
            break;
          case CycEnum.POLICY_WORDING:
            if (document.file_url) {
              this.policyWordingUrl = `${
                document.file_url
              }?timestamp=${Date.now()}`;
              break;
            }
            this.policyWordingUrl = null;
            break;
          default:
        }
      });
    }
  }

  private updateConfirmCover(isCovered: boolean, hasWellbeing: boolean): void {
    const clone = User.fromJson(this.authService.currentUser!);
    clone.confirmedCover = true;
    this.userService.updateUser(clone, false).subscribe({
      next: () => {
        if (this.authService.currentUser?.accommodation) {
          this.authService.currentUser.accommodation.covered = isCovered;
          this.authService.currentUser.accommodation.wellbeing = hasWellbeing;
          this.authService.afterUserUpdate();
        }
      },
    });
  }

  protected parseCYCBanner(page: IStrapiDocuments<CYCBanner>): void {
    this.bannerData = page.data[0].attributes;
  }

  public get coveredPolicyTypes(): PolicyType[] | undefined {
    return this.updatedAccommodation.categoryTypes?.filter(
      (policyType) => policyType.covered
    );
  }

  public get notCoveredPolicyTypes(): PolicyType[] | undefined {
    return this.updatedAccommodation.categoryTypes?.filter(
      (policyType) => !policyType.covered
    );
  }

  public replacePolicyText(description: string | null) {
    const totalSum = this.updatedAccommodation.total_sum_insured ?? '';
    const tlSum = this.updatedAccommodation.tl_sum_insured ?? '';
    const newDescription = description!
      .replace('(Total Sum Insured)', `${totalSum}`)
      .replace('(TL Sum Insured)', `${tlSum}`);

    return newDescription;
  }

  /**
   * Give a policy document type, fetch that policy documen type
   * and postfix the url with a timestamp to stop file from being cached
   * @param type
   * @returns
   */
  public getPolicyDocument(type: string): string | null {
    const docs = this.updatedAccommodation.policyDocuments;
    if (docs?.length) {
      const returnDoc = docs.find(
        (document) => document.type.toUpperCase() === type
      );
      if (returnDoc) {
        return `${returnDoc.file_url}?timestamp=${Date.now()}`;
      }
    }

    return null;
  }
}
